import React, { useState, useEffect } from 'react';
import { Eye, Lock, Users, Building, UserCheck, Globe, ChevronDown, Shield, Crown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { usePlan } from '../context/PlanContext';

// Use relative URL - works on ANY domain automatically!
const API = '/api';

/**
 * Document Visibility Control Component
 * Phase 2: Team Collaboration
 * 
 * Allows document owners to set visibility levels based on their subscription plan
 */
const DocumentVisibilityControl = ({ documentId, currentVisibility, onVisibilityChanged }) => {
  const { plan, features } = usePlan();
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (isOpen && documentId) {
      fetchVisibilityOptions();
    }
  }, [isOpen, documentId]);

  const fetchVisibilityOptions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/documents/${documentId}/visibility-options`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOptions(response.data.options);
    } catch (error) {
      console.error('Failed to fetch visibility options:', error);
      toast.error('Kunne ikke hente synlighetvalg');
    } finally {
      setLoading(false);
    }
  };

  const updateVisibility = async (newVisibility) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/documents/${documentId}/visibility`,
        { visibility: newVisibility },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Synlighet oppdatert!');
      setIsOpen(false);
      if (onVisibilityChanged) onVisibilityChanged();
    } catch (error) {
      console.error('Failed to update visibility:', error);
      const errorMsg = error.response?.data?.detail || 'Kunne ikke oppdatere synlighet';
      toast.error(errorMsg);
    } finally {
      setUpdating(false);
    }
  };

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'PRIVATE_ME':
        return <Lock className="w-4 h-4" />;
      case 'CASE_MEMBERS':
        return <Users className="w-4 h-4" />;
      case 'TEAM':
        return <Building className="w-4 h-4" />;
      case 'FIRM':
        return <Building className="w-4 h-4" />;
      case 'CLIENT':
        return <UserCheck className="w-4 h-4" />;
      case 'EXTERNAL':
        return <Globe className="w-4 h-4" />;
      default:
        return <Eye className="w-4 h-4" />;
    }
  };

  const getVisibilityColor = (visibility) => {
    switch (visibility) {
      case 'PRIVATE_ME':
        return 'bg-gray-100 text-gray-700 border-gray-300';
      case 'CASE_MEMBERS':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'TEAM':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'FIRM':
        return 'bg-purple-100 text-purple-700 border-purple-300';
      case 'CLIENT':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'EXTERNAL':
        return 'bg-orange-100 text-orange-700 border-orange-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getVisibilityLabel = (visibility) => {
    const labels = {
      'PRIVATE_ME': 'Privat',
      'CASE_MEMBERS': 'Saksmedlemmer',
      'TEAM': 'Team',
      'FIRM': 'Firma',
      'CLIENT': 'Klient',
      'EXTERNAL': 'Ekstern'
    };
    return labels[visibility] || visibility;
  };

  return (
    <div className="relative inline-block">
      {/* Current Visibility Badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all hover:shadow-md ${getVisibilityColor(currentVisibility)}`}
        title="Endre synlighet"
      >
        {getVisibilityIcon(currentVisibility)}
        {getVisibilityLabel(currentVisibility)}
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
          <div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-gray-200">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-gray-900" />
              <h3 className="text-sm font-semibold text-gray-900">Dokumentsynlighet</h3>
            </div>
            <p className="text-xs text-gray-600">Velg hvem som kan se dokumentet</p>
          </div>

          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 mx-auto"></div>
              <p className="text-sm text-gray-600 mt-2">Laster alternativer...</p>
            </div>
          ) : (
            <div className="p-2 max-h-96 overflow-y-auto">
              {options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => option.available ? updateVisibility(option.value) : null}
                  disabled={!option.available || updating}
                  className={`w-full text-left px-4 py-3 rounded-lg mb-1 transition-all ${
                    option.value === currentVisibility
                      ? 'bg-gray-100 border-2 border-gray-500'
                      : option.available
                      ? 'hover:bg-gray-50 border border-transparent'
                      : 'opacity-50 cursor-not-allowed bg-gray-50 border border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 ${option.value === currentVisibility ? 'text-gray-900' : 'text-gray-600'}`}>
                      {getVisibilityIcon(option.value)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold ${
                          option.value === currentVisibility ? 'text-indigo-900' : 'text-gray-900'
                        }`}>
                          {option.label.split(' - ')[0]}
                        </span>
                        {!option.available && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                            <Crown className="w-3 h-3" />
                            {option.upgrade_required}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {option.label.split(' - ')[1] || option.label}
                      </p>
                      {!option.available && option.upgrade_required && (
                        <p className="text-xs text-amber-700 mt-1 font-medium">
                          Krever {option.upgrade_required}-plan
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="p-3 bg-gray-50 border-t border-gray-200">
            <p className="text-xs text-gray-600 flex items-center gap-1">
              <Shield className="w-3 h-3" />
              Din plan: <span className="font-semibold text-gray-900">{plan?.plan_type || 'Free'}</span>
            </p>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default DocumentVisibilityControl;
